import { useEffect, useState } from 'react'
import {
    listTrash, restoreCustomer, purgeCustomer, restoreSubscription, purgeSubscription,
    restoreUser, purgeUser, restoreAuthor, purgeAuthor, type TrashResult,
} from '../api'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import { fill, textOf, MODULE_LABELS } from '../../shared/texts'
import InfoTip from '../components/InfoTip'
import ConfirmDialog from '../components/ConfirmDialog'
import { PAGES_TRASH_VIEW_TEXTS as T } from '../../shared/texts'

const TrashIcon = () => <img src="/icons/delete.svg" alt="" aria-hidden="true" />
// Restore = lucide "rotate-ccw" (undo).
const RestoreIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
        strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
)

type Kind = 'customers' | 'subscriptions' | 'users' | 'authors'

// One unified row across all soft-delete modules.
interface TrashRow {
    kind: Kind
    _id: string
    summary: string
    deletedAt: string
    deletedByName: string | null
    relations: Record<string, number>
}

const REL_LABELS: Record<string, string> = T.relations
// Authors are not an RBAC module of their own (they hang off `invoices`), so MODULE_LABELS
// has no entry — the badge would read „authors".
const KIND_LABELS: Record<string, string> = T.kinds

function whenDeleted(s: string) {
    return new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
}
function relSum(rel: Record<string, number>) {
    return Object.values(rel).reduce((a, b) => a + b, 0)
}
function relSummary(rel: Record<string, number>) {
    const parts = Object.entries(rel).filter(([, n]) => n > 0).map(([k, n]) => `${n} ${REL_LABELS[k] ?? k}`)
    return parts.join(', ')
}

// Flatten the trash response into one list, newest deletions first.
function flatten(data: TrashResult): TrashRow[] {
    const rows: TrashRow[] = [
        ...data.customers.map((c): TrashRow => ({ kind: 'customers', _id: c._id, summary: c.label, deletedAt: c.deletedAt, deletedByName: c.deletedByName, relations: c.relations })),
        ...data.subscriptions.map((s): TrashRow => ({ kind: 'subscriptions', _id: s._id, summary: s.label, deletedAt: s.deletedAt, deletedByName: s.deletedByName, relations: s.relations })),
        ...data.users.map((u): TrashRow => ({ kind: 'users', _id: u._id, summary: u.label, deletedAt: u.deletedAt, deletedByName: u.deletedByName, relations: u.relations ?? {} })),
        ...(data.authors ?? []).map((a): TrashRow => ({ kind: 'authors', _id: a._id, summary: fill(T.authorSummary, { label: a.label, externalRef: a.externalRef }), deletedAt: a.deletedAt, deletedByName: a.deletedByName, relations: a.relations ?? {} })),
    ]
    return rows.sort((a, b) => +new Date(b.deletedAt) - +new Date(a.deletedAt))
}

// Admin-only trash: soft-deleted records across all modules in one list, with
// restore / permanent delete (permanent delete blocked while relations exist).
export default function TrashView() {
    const { publishChange } = useTabs()
    const { toast } = useToast()
    const { can } = useAuth()
    const canRestore = can('trash', 'write')
    const canPurge = can('trash', 'delete')
    const version = useDataVersion('customers') + useDataVersion('subscriptions') + useDataVersion('users') + useDataVersion('authors')

    const [rows, setRows] = useState<TrashRow[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')

    function load() {
        setLoading(true); setError('')
        listTrash()
            .then((res) => {
                if (!res || !Array.isArray(res.customers) || !Array.isArray(res.subscriptions)) {
                    throw new Error(T.unexpectedResponse)
                }
                setRows(flatten({ ...res, users: res.users ?? [], authors: res.authors ?? [] }))
            })
            .catch((e) => setError(e?.response?.data?.error ?? e?.message ?? T.loadError))
            .finally(() => setLoading(false))
    }
    useEffect(load, [version])

    // Confirmation target for both actions.
    const [confirm, setConfirm] = useState<{ action: 'restore' | 'purge', row: TrashRow } | null>(null)
    const [busy, setBusy] = useState(false)
    const [confirmError, setConfirmError] = useState('')

    const ask = (action: 'restore' | 'purge', row: TrashRow) => { setConfirmError(''); setConfirm({ action, row }) }
    const cancel = () => { if (!busy) { setConfirm(null); setConfirmError('') } }

    const restoreFn = { customers: restoreCustomer, subscriptions: restoreSubscription, users: restoreUser, authors: restoreAuthor }
    const purgeFn = { customers: purgeCustomer, subscriptions: purgeSubscription, users: purgeUser, authors: purgeAuthor }

    async function run() {
        if (!confirm) return
        const { action, row } = confirm
        setBusy(true); setConfirmError('')
        try {
            await (action === 'restore' ? restoreFn : purgeFn)[row.kind](row._id)
            publishChange(row.kind)
            toast(action === 'restore' ? T.restored : T.purged)
            setConfirm(null)
        } catch (e: any) {
            setConfirmError(e?.response?.data?.error ?? T.actionFailed)
        } finally { setBusy(false) }
    }

    return (
        <div>
            <div className="row" style={{ marginBottom: 18 }}>
                <div className="page-title">
                    <h1>{T.title}</h1>
                    <InfoTip>{T.info}</InfoTip>
                </div>
            </div>

            {error && <div className="notice err">{error}</div>}

            <div className="panel">
                {loading ? <p className="muted">{T.loading}</p> : rows.length === 0 ? (
                    <p className="muted">{T.empty}</p>
                ) : (
                    <table>
                        <thead>
                            <tr><th>{T.columns.entry}</th><th>{T.columns.deletedAt}</th><th>{T.columns.deletedBy}</th><th>{T.columns.relations}</th><th></th></tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => {
                                const blocked = relSum(r.relations) > 0
                                return (
                                    <tr key={`${r.kind}-${r._id}`}>
                                        <td>
                                            <span className="badge" style={{ marginRight: 8 }}>{KIND_LABELS[r.kind] ?? textOf(MODULE_LABELS, r.kind)}</span>
                                            {r.summary}
                                        </td>
                                        <td>{whenDeleted(r.deletedAt)}</td>
                                        <td>{r.deletedByName ?? '—'}</td>
                                        <td>{blocked ? relSummary(r.relations) : '—'}</td>
                                        <td className="row-actions">
                                            {canRestore && (
                                                <button className="icon-action" title={T.restore} aria-label={T.restore}
                                                    onClick={() => ask('restore', r)}><RestoreIcon /></button>
                                            )}
                                            {canPurge && (
                                                <button className="icon-action" disabled={blocked}
                                                    title={blocked ? fill(T.purgeBlocked, { relations: relSummary(r.relations) }) : T.purge}
                                                    aria-label={T.purge} onClick={() => ask('purge', r)}><TrashIcon /></button>
                                            )}
                                            {!canRestore && !canPurge && <span className="muted">—</span>}
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <ConfirmDialog
                open={!!confirm}
                title={confirm?.action === 'restore' ? T.restore : T.purge}
                message={confirm
                    ? confirm.action === 'restore'
                        ? <>{fill(T.restoreQuestion, { summary: confirm.row.summary })}</>
                        : <>{fill(T.purgeQuestion, { summary: confirm.row.summary })}</>
                    : null}
                confirmLabel={confirm?.action === 'restore' ? T.restore : T.purge}
                danger={confirm?.action === 'purge'}
                busy={busy}
                error={confirmError}
                onConfirm={run}
                onCancel={cancel}
            />
        </div>
    )
}
